import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import {
  clearSession,
  createActiv8Message,
  deleteActiv8Message,
  deleteMultipleActiv8Messages,
  editActiv8Message,
  generateActiv8Messages,
  getActiv8MessageLogs,
  replyActiv8Messages,
  saveActiv8Message,
  saveMessageChanges,
  searchActiv8Messages,
  adjustMessages,
} from './activ8MessagesActions';
import { ACTIV8_MESSAGE_GENERATE_TABS, ACTIV8_MESSAGE_TABS } from '../../constants';
import MOCKED_MESSAGES from '../../activ8/messages/adjust/adjusted-messages/mocked';

const initialState = {
  messagesTab: ACTIV8_MESSAGE_TABS.GENERATE,
  generateTab: ACTIV8_MESSAGE_GENERATE_TABS.GENERATED,

  generationForm: {
    byPass: '',
    focusOn: '',
    url: '',
    fileText: '',
    hint: '',
  },

  threadId: '',

  isGenerating: false,
  isFormLoading: false,
  isResetting: false,
  isCreating: false,

  messagesContent: [],
  savedMessagesContent: [],
  isMessagesLoading: false,

  tableSettings: {
    sort: '-count',
    searchQuery: '',
  },
  savedTableSettings: {
    sort: '-count',
    searchQuery: '',
  },

  messageLogs: {
    histories: [],
    original: {},
  },
  isMessageLogsLoading: false,

  isDirty: false,
  editableMessage: {
    id: null,
    message: '',
  },

  initialAdjustMessage: '',
  adjustedMessages: MOCKED_MESSAGES,
  // adjustedMessages: [],
  isAdjusting: false,
};

const formatGeneratedMessages = (messages, startCount) =>
  messages.map((message, index) => ({
    message,
    count: startCount + messages.length - index,
    newMessage: true,
    id: uuidv4(),
    savedId: null,
  }));

const formatPersistedMessages = (messages) =>
  messages.map(({ message, count, id, reaction, selected, isManual, savedId }) => ({
    message,
    count,
    id,
    reaction,
    selected,
    isManual,
    newMessage: false,
    savedId,
  }));

const activ8MessagesSlice = createSlice({
  name: 'activ8Messages',
  initialState,
  reducers: {
    changeMessagesTab: (state, action) => {
      state.messagesTab = action.payload;
    },
    changeGenerateTab: (state, action) => {
      state.generateTab = action.payload;
    },
    changeGenerationForm: (state, action) => {
      state.generationForm = { ...state.generationForm, ...action.payload };
    },

    changeTableSettings: (state, action) => {
      state.tableSettings = { ...state.tableSettings, ...action.payload };
    },
    changeSavedTableSettings: (state, action) => {
      state.savedTableSettings = { ...state.savedTableSettings, ...action.payload };
    },

    changeLocalMessage: (state, action) => {
      state.messagesContent = state.messagesContent.map((message) =>
        message.id === action.payload.id ? { ...message, message: action.payload.message } : { ...message },
      );
    },

    setIsDirty: (state, action) => {
      state.isDirty = action.payload;
    },
    setEditableMessage: (state, action) => {
      state.editableMessage = action.payload;
    },
    changeEditableMessage: (state, action) => {
      state.editableMessage.message = action.payload;
    },

    likeLocalMessage: (state, action) => {
      state.messagesContent = state.messagesContent.map((message) =>
        message.id === action.payload.id ? { ...message, reaction: action.payload.reaction } : { ...message },
      );
    },

    clearLocalMessages: (state) => {
      state.messagesContent = [];
    },

    changeInitialAdjustMessage: (state, action) => {
      state.initialAdjustMessage = action.payload;
    },

    changeAdjustedMessage: (state, action) => {
      const { id, ...changes } = action.payload;

      state.adjustedMessages = state.adjustedMessages.map((message) =>
        message.id === id ? { ...message, ...changes } : message,
      );
    },

    removeAdjustedMessage: (state, action) => {
      state.adjustedMessages = state.adjustedMessages.filter((message) => message.id !== action.payload);
    },
    clearAdjustedMessages: (state) => {
      state.adjustedMessages = [];
    },

    resetMessageLogs: (state) => {
      state.messageLogs = initialState.messageLogs;
    },

    resetParams: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(isAnyOf(generateActiv8Messages.fulfilled), (state, action) => {
        const { messages, threadId, hint } = action.payload;

        state.messagesContent = formatGeneratedMessages(messages, 0);
        state.threadId = threadId;
        state.generationForm.hint = hint || '';
        state.isGenerating = false;
      })
      .addMatcher(isAnyOf(generateActiv8Messages.pending), (state, action) => {
        state.isGenerating = true;
        state.generationForm.url = action.meta.arg.values.url;
        state.generationForm.byPass = action.meta.arg.values.byPass;
        state.generationForm.focusOn = action.meta.arg.values.focusOn;
        state.generationForm.fileText = action.meta.arg.values.fileText;
      })
      .addMatcher(isAnyOf(generateActiv8Messages.rejected), (state) => {
        state.isGenerating = false;
      })

      .addMatcher(isAnyOf(replyActiv8Messages.fulfilled), (state, action) => {
        const { messages, threadId, hint } = action.payload;

        const persistedMessages = state.messagesContent;
        const startCount = persistedMessages.length;

        const formattedNewMessages = formatGeneratedMessages(messages, startCount);
        const formattedOldMessages = formatPersistedMessages(persistedMessages);

        state.messagesContent = [...formattedNewMessages, ...formattedOldMessages];
        state.threadId = threadId;
        state.generationForm.hint = hint || '';
        state.isFormLoading = false;
      })
      .addMatcher(isAnyOf(replyActiv8Messages.pending), (state) => {
        state.isFormLoading = true;
      })
      .addMatcher(isAnyOf(replyActiv8Messages.rejected), (state) => {
        state.isFormLoading = false;
      })

      .addMatcher(isAnyOf(clearSession.fulfilled), (state) => {
        state.messagesContent = [];
        state.generationForm = initialState.generationForm;
        state.threadId = '';
        state.isResetting = false;
      })
      .addMatcher(isAnyOf(clearSession.pending), (state) => {
        state.isResetting = true;
      })
      .addMatcher(isAnyOf(clearSession.rejected), (state) => {
        state.isResetting = false;
      })

      .addMatcher(isAnyOf(searchActiv8Messages.fulfilled), (state, action) => {
        state.savedMessagesContent = action.payload.map((message) => ({ ...message, savedId: message.id }));
        state.isMessagesLoading = false;
      })
      .addMatcher(isAnyOf(searchActiv8Messages.pending), (state) => {
        state.isMessagesLoading = true;
      })
      .addMatcher(isAnyOf(searchActiv8Messages.rejected), (state) => {
        state.isMessagesLoading = false;
      })

      .addMatcher(isAnyOf(createActiv8Message.fulfilled), (state, action) => {
        const formattedNewMessages = action.payload.map((message) => ({
          ...message,
          savedId: message.id,
        }));
        state.savedMessagesContent = [...formattedNewMessages, ...state.messagesContent];
        state.isCreating = false;
      })
      .addMatcher(isAnyOf(createActiv8Message.pending), (state) => {
        state.isCreating = true;
      })
      .addMatcher(isAnyOf(createActiv8Message.rejected), (state) => {
        state.isCreating = false;
      })

      .addMatcher(isAnyOf(deleteActiv8Message.fulfilled), (state, action) => {
        if (state.generateTab === ACTIV8_MESSAGE_GENERATE_TABS.GENERATED) {
          state.messagesContent = state.messagesContent.filter((message) => message.id !== action.payload);
        } else {
          state.savedMessagesContent = state.savedMessagesContent.filter((message) => message.id !== action.payload);
        }
        state.isMessagesLoading = false;
      })
      .addMatcher(isAnyOf(deleteActiv8Message.pending), (state) => {
        state.isMessagesLoading = true;
      })
      .addMatcher(isAnyOf(deleteActiv8Message.rejected), (state) => {
        state.isMessagesLoading = false;
      })

      .addMatcher(isAnyOf(saveActiv8Message.fulfilled), (state, action) => {
        const savedMessage = action.payload.savedMessage;
        const localMessageId = action.payload.id;
        state.messagesContent = state.messagesContent.map((message) =>
          message.id === localMessageId
            ? { ...message, ...savedMessage, id: message.id, savedId: savedMessage.id, count: message.count }
            : { ...message },
        );
        state.isMessagesLoading = false;
      })
      .addMatcher(isAnyOf(saveActiv8Message.pending), (state) => {
        state.isMessagesLoading = true;
      })
      .addMatcher(isAnyOf(saveActiv8Message.rejected), (state) => {
        state.isMessagesLoading = false;
      })

      .addMatcher(isAnyOf(editActiv8Message.fulfilled, saveMessageChanges.fulfilled), (state, action) => {
        const updatedMessage = action.payload.updatedMessage;
        const localMessageId = action.payload.localId;

        if (state.generateTab === ACTIV8_MESSAGE_GENERATE_TABS.GENERATED) {
          state.messagesContent = state.messagesContent.map((message) =>
            message.id === localMessageId
              ? { ...message, ...updatedMessage, id: localMessageId, savedId: updatedMessage.id, count: message.count }
              : { ...message },
          );
        } else {
          state.savedMessagesContent = state.savedMessagesContent.map((message) =>
            message.id === localMessageId
              ? { ...message, ...updatedMessage, id: localMessageId, savedId: updatedMessage.id, count: message.count }
              : { ...message },
          );
        }
        state.isMessagesLoading = false;
      })
      .addMatcher(isAnyOf(editActiv8Message.pending, saveMessageChanges.pending), (state) => {
        state.isMessagesLoading = true;
      })
      .addMatcher(isAnyOf(editActiv8Message.rejected, saveMessageChanges.rejected), (state) => {
        state.isMessagesLoading = false;
      })

      .addMatcher(isAnyOf(deleteMultipleActiv8Messages.fulfilled), (state) => {
        state.savedMessagesContent = [];
        state.isMessagesLoading = false;
      })
      .addMatcher(isAnyOf(deleteMultipleActiv8Messages.pending), (state) => {
        state.isMessagesLoading = true;
      })
      .addMatcher(isAnyOf(deleteMultipleActiv8Messages.rejected), (state) => {
        state.isMessagesLoading = false;
      })

      .addMatcher(isAnyOf(getActiv8MessageLogs.fulfilled), (state, action) => {
        state.messageLogs.histories = action.payload.histories;
        state.messageLogs.original = action.payload.original;
        state.isMessageLogsLoading = false;
      })
      .addMatcher(isAnyOf(getActiv8MessageLogs.pending), (state) => {
        state.isMessageLogsLoading = true;
      })
      .addMatcher(isAnyOf(getActiv8MessageLogs.rejected), (state) => {
        state.isMessageLogsLoading = false;
      })

      .addMatcher(isAnyOf(adjustMessages.fulfilled), (state, action) => {
        const formattedMessages = formatGeneratedMessages(action.payload, 0);
        state.adjustedMessages = formattedMessages;
        state.isAdjusting = false;
      })
      .addMatcher(isAnyOf(adjustMessages.pending), (state) => {
        state.isAdjusting = true;
      })
      .addMatcher(isAnyOf(adjustMessages.rejected), (state) => {
        state.isAdjusting = false;
      });
  },
});

export const {
  changeMessagesTab,
  changeGenerateTab,

  changeGenerationForm,
  changeTableSettings,
  changeSavedTableSettings,
  changeLocalMessage,
  setIsDirty,
  setEditableMessage,
  changeEditableMessage,
  likeLocalMessage,

  clearLocalMessages,
  resetMessageLogs,
  resetParams,
  changeInitialAdjustMessage,

  changeAdjustedMessage,
  removeAdjustedMessage,
  clearAdjustedMessages,
} = activ8MessagesSlice.actions;

export default activ8MessagesSlice.reducer;
